import { useCallback, useEffect, useReducer, useRef } from "react";
import { getLivePreviewOrigin } from "helpers/landingUrl";
import { fetchLivePreviewAvailability, requestLivePreviewSession } from "./pagesApi";
import {
  MINT_TIMEOUT_MS,
  READY_TIMEOUT_MS,
  afterSaveAction,
  focusPathFrom,
  initialLivePreviewState,
  isOpen,
  isPreviewSessionUrl,
  isTrustedPreviewMessage,
  livePreviewReducer,
  mintErrorCode,
  remintDelayMs,
} from "./livePreview";

/**
 * The effects around the live-preview state machine (DAB-861): the
 * availability probe, minting, the message listener, the ready timeout and the
 * re-mint timer. Every decision is `livePreview.js`'s; this only wires it to
 * the network, the window and the clock.
 *
 * `onFocusField(path)` runs when the editor clicks CMS text in the frame
 * (DAB-902), and only for a message that passed the same origin and frame checks.
 */
export default function useLivePreview({ pageKey, lang, onFocusField }) {
  const [state, dispatch] = useReducer(livePreviewReducer, initialLivePreviewState);
  const frameRef = useRef(null);
  const origin = getLivePreviewOrigin();
  // Through a ref, so the message listener is not re-attached on every render.
  const onFocusRef = useRef(onFocusField);
  onFocusRef.current = onFocusField;

  useEffect(() => {
    // No usable preview origin (unset, not https, or admin's own origin, where
    // the same-origin sandbox would hand the frame admin's storage): the
    // feature does not exist here, so do not even ask the API.
    if (!origin) {
      dispatch({ type: "availability", available: false });
      return undefined;
    }
    let cancelled = false;
    fetchLivePreviewAvailability()
      .then((data) => {
        if (!cancelled) dispatch({ type: "availability", available: data?.available === true });
      })
      // Any failure hides the toggle: a 503 `preview_unavailable` means this
      // environment has no preview, and an unreachable probe is no better a
      // moment to offer one.
      .catch(() => {
        if (!cancelled) dispatch({ type: "availability", available: false });
      });
    return () => {
      cancelled = true;
    };
  }, [origin]);

  // One mint per nonce. `open`, `retry`, `remint` and session-* notices all
  // just bump it; a response for an older nonce is dropped by the reducer.
  const { status, mintNonce } = state;
  useEffect(() => {
    if (status !== "minting") return undefined;
    const nonce = mintNonce;
    // ApiService has no timeout option; the reducer drops the late answer.
    const timer = setTimeout(() => dispatch({ type: "mint-timeout", nonce }), MINT_TIMEOUT_MS);
    requestLivePreviewSession(pageKey, lang)
      .then((data) => {
        if (!isPreviewSessionUrl(data?.url, origin)) {
          dispatch({ type: "mint-failed", nonce, code: "origin-mismatch" });
        } else {
          dispatch({ type: "minted", nonce, url: data.url, expiresAt: data.expiresAt });
        }
      })
      .catch((error) => dispatch({ type: "mint-failed", nonce, code: mintErrorCode(error) }));
    return () => clearTimeout(timer);
    // pageKey/lang are read at mint time; a change re-mints through `remint` below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status === "minting", mintNonce]);

  // A new page or language needs a session landing on its own path.
  const target = `${pageKey}|${lang}`;
  const lastTarget = useRef(target);
  useEffect(() => {
    if (target === lastTarget.current) return;
    lastTarget.current = target;
    dispatch({ type: "remint", reason: "retarget" });
  }, [target]);

  useEffect(() => {
    const onMessage = (event) => {
      if (!isTrustedPreviewMessage(event, { origin, frameWindow: frameRef.current?.contentWindow })) {
        return;
      }
      const focusPath = focusPathFrom(event.data);
      if (focusPath) onFocusRef.current?.(focusPath);
      else dispatch({ type: "message", data: event.data });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [origin]);

  useEffect(() => {
    if (status !== "loading") return undefined;
    const attempt = state.attempt;
    const timer = setTimeout(() => dispatch({ type: "timeout", attempt }), READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [status, state.attempt]);

  const isLive = status === "loading" || status === "ready";
  useEffect(() => {
    if (!isLive || !state.expiresAt) return undefined;
    const timer = setTimeout(
      () => dispatch({ type: "remint", reason: "expiring" }),
      remintDelayMs(state.expiresAt),
    );
    return () => clearTimeout(timer);
  }, [isLive, state.expiresAt]);

  // Read through a ref so the editor's `save()` always sees the latest state.
  const stateRef = useRef(state);
  stateRef.current = state;

  /** Call after every successful draft save. */
  const notifySaved = useCallback(() => {
    const action = afterSaveAction(stateRef.current);
    if (action === "reload") {
      frameRef.current?.contentWindow?.postMessage({ type: "cms:reload" }, origin);
    } else if (action === "remint") {
      dispatch({ type: "remint", reason: "expiring" });
    }
  }, [origin]);

  return {
    state,
    frameRef,
    isAvailable: state.available === true,
    isOpen: isOpen(state),
    open: useCallback(() => dispatch({ type: "open" }), []),
    close: useCallback(() => dispatch({ type: "close" }), []),
    retry: useCallback(() => dispatch({ type: "retry" }), []),
    notifySaved,
  };
}
